import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PublicHeader } from '../components/layout/PublicHeader';
import { PublicFooter } from '../components/layout/PublicFooter';
import { getBlogPosts, BlogPost } from '../services/api';

export const Blog: React.FC = () => {
    const [posts, setPosts] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    useEffect(() => {
        const fetchInitialPosts = async () => {
            try {
                const data = await getBlogPosts(1);
                setPosts(data.results);
                setHasMore(!!data.next);
            } catch (error) {
                console.error("Failed to fetch blog posts:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchInitialPosts();
    }, []);

    const handleLoadMore = async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        const nextPage = page + 1;
        try {
            const data = await getBlogPosts(nextPage);
            setPosts(prev => [...prev, ...data.results]);
            setHasMore(!!data.next);
            setPage(nextPage);
        } catch (error) {
            console.error("Failed to load more posts:", error);
        } finally {
            setLoadingMore(false);
        }
    };

    return (
        <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col transition-colors duration-200 font-body">
            <PublicHeader />

            <div className="flex-1 w-full max-w-[1400px] mx-auto px-4 sm:px-10 pt-28 pb-12">
                <div className="text-center mb-16">
                    <span className="text-brand font-bold tracking-wider uppercase text-sm mb-4 block">Blog & Recursos</span>
                    <h1 className="text-4xl sm:text-5xl font-bold font-display text-slate-900 dark:text-white mb-6">
                        Insights de Tecnología
                    </h1>
                    <p className="text-slate-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
                        Noticias, tutoriales y análisis profundo sobre ciberseguridad, desarrollo de software y transformación digital.
                    </p>
                </div>

                {loading ? (
                    <div className="flex justify-center p-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                            {posts.map((post) => (
                                <Link to={`/blog/${post.slug}`} key={post.id} className="flex flex-col bg-white dark:bg-[#1a2332] border border-gray-200 dark:border-[#282e39] rounded-xl overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer group">
                                    <div className="h-48 bg-slate-800 relative overflow-hidden">
                                        {post.image ? (
                                            <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                        ) : (
                                            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-tr from-blue-900 to-slate-900">
                                                <span className="material-symbols-outlined text-6xl text-white/20 group-hover:scale-110 transition-transform duration-500">article</span>
                                            </div>
                                        )}
                                        <div className="absolute top-4 left-4">
                                            <span className="px-3 py-1 bg-black/50 backdrop-blur-md text-white rounded-full text-xs font-bold border border-white/10 uppercase">{post.category}</span>
                                        </div>
                                    </div>
                                    <div className="p-6 flex-1 flex flex-col">
                                        <div className="text-xs text-gray-500 mb-3 flex items-center gap-2">
                                            <span>{new Date(post.published_at).toLocaleDateString()}</span> • <span>{post.read_time} min lectura</span>
                                        </div>
                                        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3 group-hover:text-brand transition-colors line-clamp-2">
                                            {post.title}
                                        </h2>
                                        <p className="text-slate-600 dark:text-gray-400 text-sm mb-4 flex-1 line-clamp-3">
                                            {post.summary}
                                        </p>
                                        <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100 dark:border-white/5">
                                            <div className="size-8 rounded-full bg-gray-700 flex items-center justify-center text-xs text-white font-bold">
                                                {post.author.charAt(0).toUpperCase()}
                                            </div>
                                            <span className="text-xs text-gray-500">Por {post.author}</span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>

                        {posts.length === 0 && (
                            <div className="text-center py-12 text-gray-400">
                                No se encontraron artículos.
                            </div>
                        )}

                        <div className="text-center">
                            {hasMore && (
                                <button
                                    onClick={handleLoadMore}
                                    disabled={loadingMore}
                                    className="px-8 py-3 border border-gray-300 dark:border-white/10 text-slate-700 dark:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-white/5 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                                >
                                    {loadingMore ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Cargando...
                                        </>
                                    ) : (
                                        'Cargar más artículos'
                                    )}
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>

            <PublicFooter />
        </div>
    );
};
